import { styled } from '@mui/material';
import { UploadsType } from '@prisma/client';
import { FilePond } from 'react-filepond';
import { useState } from 'react';
import { baseUrl } from '../../api/api';
import { getStorage } from '../../utils/localOrSessionStorage';

const StyledFilePond = styled(FilePond)(() => ({
  '.filepond--item': {
    width: '100%',
  },
  '@media (min-width: 50em)': {
    '.filepond--item': {
      width: 'calc(33.33% - 0.5em)',
    },
  },
  '.filepond--wrapper': {
    height: 50,
  },
}));

interface Props {
  listingId: string;
  userId?: string;
  docType?: UploadsType;
  analyze: boolean;
  onUpload?: (e: any) => void;
  onUploadResponse?: (e: any) => void;
  buyerId?: string;
  maxFiles?: number;
  token?: string;
  /** Set when an admin is uploading this file for the seller, not themselves —
   * disambiguates admin intent for hooks that can't tell from identity alone. */
  onBehalfOfSeller?: boolean;
}

const FileUpload: React.FC<Props> = ({
  listingId,
  userId,
  onUpload,
  onUploadResponse,
  docType,
  analyze,
  buyerId,
  maxFiles = 10,
  token: publicToken,
  onBehalfOfSeller,
}) => {
  const [files, setFiles] = useState([]);
  const jwtToken = getStorage().getItem('jwtToken');
  const token = publicToken || jwtToken;

  return (
    <StyledFilePond
      files={files as any}
      onupdatefiles={(e) => setFiles([...files, ...e] as any)}
      allowMultiple={true}
      imagePreviewMaxHeight={150}
      maxFiles={maxFiles}
      allowRevert={false}
      credits={false}
      server={{
        process: (
          _fieldName,
          file,
          _metadata,
          load,
          error,
          progress,
          abort
        ) => {
          const formData = new FormData();
          formData.append('listingId', listingId);
          if (userId) {
            formData.append('userId', userId);
          }
          formData.append('type', docType ?? 'TRANSACTION_DOC');
          formData.append('analyze', analyze ? 'true' : 'false');
          formData.append('file', file);
          if (buyerId) {
            formData.append('buyerId', buyerId);
          }
          if (onBehalfOfSeller) {
            formData.append('onBehalfOfSeller', 'true');
          }
          // If publicToken is provided, include it in the form data for the backend
          if (publicToken) {
            formData.append('token', publicToken);
          }
          const request = new XMLHttpRequest();
          request.open('POST', `${baseUrl}/uploads`);
          // Only set Authorization header if we have a token (JWT or public)
          if (token) {
            request.setRequestHeader('Authorization', `Bearer ${token}`);
          }

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
              onUploadResponse?.(request.responseText);
            } else {
              error('Upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      }}
      onprocessfile={(e) => onUpload?.(e)}
      name="files"
      // eslint-disable-next-line max-len
      labelIdle='Drag & Drop your files to upload or <span class="filepond--label-action">Browse</span>'
    />
  );
};

export default FileUpload;
